import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, Dropdown, Grid, Icon, InputText, Text } from "~/components-shared";
import { useDispatch, useSelector } from "react-redux";
import { AuthoritiesEnum, StatusStateEnum } from "~/types/enums";
import styled, { useTheme } from "styled-components";
import useAbacExt from "~/hooks/abac";
import topicActionCreators from "~/store/topics/actions";
import topicSelectors from "~/store/topics/selectors";
import { TopicActionEnum } from "~/store/topics/types";
import topicFootprintActionCreators from "~/store/topicFootprints/actions";
import topicFootprintSelectors from "~/store/topicFootprints/selectors";
import { TopicFootprintActionEnum } from "~/store/topicFootprints/types";
const FixedCard = styled(Card)`
  position: sticky;
  bottom: 1em;
  z-index: 2;
  width: initial;
`;
const StyledDropdown = styled(Dropdown)`
  display: inline-block;
`;
const LabelColor = styled.i`
  background-color: ${_ref => {
  let {
    $color
  } = _ref;
  return $color;
}};
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  margin: 0.2rem 1rem 0 0;
  display: block;
`;
const StyledListItem = styled(Dropdown.ListItem)`
  position: sticky;
  bottom: 0;
  background-color: ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.colors.background.primary;
}};

  &:hover {
    background-color: ${_ref3 => {
  let {
    theme
  } = _ref3;
  return theme.colors.background.primary;
}};
    span,
    i {
      color: ${_ref4 => {
  let {
    theme
  } = _ref4;
  return theme.colors.text.primary;
}};
    }
  }
`;
const TopicFootprintSelectionCard = _ref5 => {
  let {
    topic,
    selectionCount,
    selection,
    setSelection
  } = _ref5;
  const {
    colors
  } = useTheme();
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const {
    userHas
  } = useAbacExt();
  const status = useSelector(topicFootprintSelectors.getBulkLabelStatus(topic.id));
  const labels = useSelector(topicSelectors.getLabels);
  const [searchValue, setSearchValue] = useState("");
  const filteredLabels = labels.filter(label => `${label.title}.${label.description}`.toLowerCase().includes(searchValue.toLowerCase()) && (!label.topic || label.topic.id === topic.id));
  const handleAddLabel = labelId => {
    dispatch(topicFootprintActionCreators.bulkLabelStarted(topic.id, selection, labelId));
  };
  const handleManageLabels = () => {
    dispatch(topicActionCreators.actionChanged(TopicActionEnum.MANAGE_LABELS, topic.id));
  };
  const handleSearch = query => {
    setSearchValue(query);
  };
  const deleteFootprints = () => {
    dispatch(topicFootprintActionCreators.actionChanged(TopicFootprintActionEnum.BULK_DELETE, null, {
      topicId: topic.id,
      footprintIds: selection || []
    }));
  };
  const moveFootprints = () => {
    dispatch(topicFootprintActionCreators.actionChanged(TopicFootprintActionEnum.BULK_MOVE, null, {
      topicId: topic.id,
      footprintIds: selection,
      count: selection.includes("*") ? topic.footprintCount - selection.length + 1 : selection.length
    }));
  };
  return /*#__PURE__*/React.createElement(FixedCard, {
    s: {
      p: 2
    },
    shadow: true,
    color: "background",
    align: "left",
    maxWidth: "26rem"
  }, /*#__PURE__*/React.createElement(Grid, {
    cols: 8,
    gap: 2,
    template: "auto auto auto auto 1fr auto",
    align: "center",
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Button, {
    onClick: () => {
      if (selection.includes("*")) {
        setSelection([], true);
      } else {
        setSelection(["*"], true);
      }
    },
    color: "transparent",
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": selection.includes("*") ? t("TopicFootprintSelectionCard.deselectAll") : t("TopicFootprintSelectionCard.selectAll"),
    outline: false,
    s: {
      p: 2
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    scale: 5,
    color: "primary",
    src: selection.includes("*") ? "checkboxMinus" : "checkboxFill"
  })), /*#__PURE__*/React.createElement(Button, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("TopicFootprintSelectionCard.deleteItems", {
      count: selectionCount
    }),
    onClick: deleteFootprints,
    color: "transparent",
    outline: false,
    s: {
      p: 2
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    scale: 5,
    src: "bin"
  })), /*#__PURE__*/React.createElement(Button, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("TopicFootprintSelectionCard.moveItems", {
      count: selectionCount
    }),
    onClick: moveFootprints,
    color: "transparent",
    outline: false,
    s: {
      p: 2
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    scale: 5,
    src: "share"
  })), /*#__PURE__*/React.createElement(StyledDropdown, {
    direction: "topRight",
    handlerProps: {
      color: "transparent",
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-content": t("TopicFootprintLabels.labels"),
      fontSize: 3,
      outline: false,
      s: {
        p: 2
      },
      loading: status === StatusStateEnum.LOADING
    },
    handler: /*#__PURE__*/React.createElement(Icon, {
      scale: 5,
      src: "sell"
    }),
    maxWidth: "260px"
  }, _ref6 => {
    let {
      isOpen,
      onItemClick
    } = _ref6;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      noHover: true,
      sticky: true
    }, /*#__PURE__*/React.createElement(InputText, {
      autoFocus: true,
      value: searchValue,
      onChange: handleSearch,
      elemBefore: /*#__PURE__*/React.createElement(Icon, {
        src: "search",
        s: {
          mr: 2
        }
      }),
      placeholder: t("common.actions.search"),
      clearButton: true
    })), filteredLabels.map(label => /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        handleAddLabel(label.id);
        handleSearch("");
        onItemClick();
      },
      key: label.id,
      textWrap: true
    }, /*#__PURE__*/React.createElement(Grid, {
      cols: 2,
      gap: 2,
      template: "1rem auto"
    }, /*#__PURE__*/React.createElement(LabelColor, {
      $color: label.color
    }), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
      display: "block",
      fontSize: 3
    }, label.title), label.description && /*#__PURE__*/React.createElement(Text, {
      fontSize: 3,
      color: "textSecondary",
      s: {
        mt: 1
      },
      display: "block"
    }, label.description))))), userHas(AuthoritiesEnum.TOPICS_WRITE) && /*#__PURE__*/React.createElement(StyledListItem, {
      onClick: () => {
        onItemClick();
        handleManageLabels();
      },
      textWrap: true
    }, /*#__PURE__*/React.createElement(Grid, {
      cols: 2,
      gap: 2,
      template: "1rem auto",
      align: "center"
    }, /*#__PURE__*/React.createElement(Icon, {
      color: colors.text.secondary,
      src: "cog",
      scale: 3
    }), /*#__PURE__*/React.createElement(Text, {
      color: "textSecondary",
      fontSize: 3
    }, t("TopicFootprintLabels.manageLabels")))));
  }), /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary"
  }, t("TopicFootprintSelectionCard.selected", {
    count: selectionCount
  })), /*#__PURE__*/React.createElement(Button, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("TopicFootprintSelectionCard.cancelSelection"),
    onClick: () => setSelection([], true),
    color: "transparent",
    outline: false,
    s: {
      p: 2
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    scale: 5,
    src: "cross"
  }))));
};
export default TopicFootprintSelectionCard;